import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import { storefrontClient } from '../apollo/shopify/storefrontClient';
import {
  AvailableDraw,
  DrawGateway,
  DrawWithFrames,
  PreAuthDrawController,
} from '../features/draws/types';
import { ShopifyProductsGateway } from '../gateways/ShopifyProductsGateway';
import { ApplicationState } from '../store/types';
import { useAuth } from './AuthProvider';

const shopifyProductsGateway = new ShopifyProductsGateway(storefrontClient);

interface DrawContextProps {
  children: ReactNode;
  initialSelectedDraws?: string[];
  mainDraw?: AvailableDraw;
  linkedDraws: AvailableDraw[];
  drawGateway: DrawGateway;
}

type DrawContextType = {
  mainDraw?: DrawWithFrames;
  linkedDraws: DrawWithFrames[];
  isAccountInDraw: (drawId: string) => boolean;
  // selectedDraws is used to store the draws that the user has selected to enter. This is used for draws that are linked so the user selects draws before going on to enter them
  selectedDraws: string[];
  selectDraw: (drawId: string) => void;
  winningDrawsCount: number;
  setWinningDrawsCount: (count: number) => void;
  drawIdsEntered: string[];
  accountAuctionBid: (drawId: string) =>
    | {
        amount: string;
        currencyCode: string;
      }
    | undefined;
  currentWinningBidByDrawId: Record<
    string,
    { amount: string; currencyCode: string } | undefined
  >;
  updateCurrentWinningBidByDrawId: (
    drawId: string,
    winningFloorPrice: { amount: string; currencyCode: string } | undefined,
  ) => void;
  updateDrawEntryStatus: (drawId?: string) => void;
  setSelectedDraws: (drawIds: string[]) => void;
  drawEntriesInSession: PreAuthDrawController['entryIntentResponse'][];
  addDrawEntryToSession: (
    drawEntries: PreAuthDrawController['entryIntentResponse'],
  ) => void;
};

const DrawContext = createContext<DrawContextType>({
  mainDraw: undefined,
  linkedDraws: [] as DrawWithFrames[],
  isAccountInDraw: () => false,
  selectedDraws: [],
  selectDraw: () => {},
  winningDrawsCount: 0,
  setWinningDrawsCount: () => {},
  drawIdsEntered: [],
  accountAuctionBid: () => undefined,
  currentWinningBidByDrawId: {},
  updateCurrentWinningBidByDrawId: () => {},
  updateDrawEntryStatus: () => {},
  setSelectedDraws: () => {},
  drawEntriesInSession: [],
  addDrawEntryToSession: () => {},
});

interface DrawEntryStatus {
  entered: boolean;
  accountAuctionBid?: {
    amount: string;
    currencyCode: string;
  };
}

export const DrawProvider = (props: DrawContextProps) => {
  const { children, drawGateway, initialSelectedDraws, linkedDraws, mainDraw } =
    props;
  const { user } = useAuth();
  const [mainDrawEnriched, setMainDrawEnriched] = useState<
    DrawWithFrames | undefined
  >(mainDraw);
  const [linkedDrawsEnriched, setLinkedDrawsEnriched] =
    useState<DrawWithFrames[]>(linkedDraws);
  const [selectedDraws, setSelectedDraws] = useState<string[]>(
    initialSelectedDraws ?? (mainDraw?.draw.id ? [mainDraw.draw.id] : []),
  );
  const selectedForDrawIdRef = useRef(mainDraw?.draw.id);
  const [currentWinningBidByDrawId, setCurrentWinningBidByDrawId] = useState<
    Record<string, { amount: string; currencyCode: string } | undefined>
  >({});
  const [winningDrawsCount, setWinningDrawsCount] = useState(
    initialSelectedDraws?.length || 1,
  );
  const [drawEntriesInSession, setDrawEntriesInSession] = useState<
    PreAuthDrawController['entryIntentResponse'][]
  >([]);
  // redux for when user has just entered the draw and a refresh has not been done
  const drawEntries = useSelector(
    (s: ApplicationState) => s.features.draw.drawEntry,
  );

  const allDraws = useMemo(
    () => [...linkedDraws, mainDraw],
    [linkedDraws, mainDraw],
  );

  const [drawEntryStatus, setDrawEntryStatus] = useState<
    Record<string, DrawEntryStatus>
  >(() => {
    const initialStatus: Record<string, DrawEntryStatus> = {};
    allDraws.forEach((draw) => {
      if (draw?.draw.id) {
        initialStatus[draw.draw.id] = {
          entered: false,
          accountAuctionBid: undefined,
        };
      }
    });
    return initialStatus;
  });

  const isDrawEnriched = (draw: AvailableDraw): draw is DrawWithFrames =>
    Boolean((draw as DrawWithFrames).purchaseDetailsEnriched);

  const enrichDraw = async (draw: DrawWithFrames): Promise<DrawWithFrames> => {
    const data = await shopifyProductsGateway.getProductPurchaseDetails({
      frameSource: 'currentProduct',
      identifier: {
        id: draw.draw.productId,
      },
      releaseName: 'draw',
    });
    return {
      ...draw,
      purchaseDetailsEnriched: true,
      product: {
        ...draw.product,
        // override default variant price with the price retrived when given the right release name
        price: data?.price ?? draw.product.price,
      },
      frames: data?.frames,
      frameRecommendedTitle: data?.frameRecommendedTitle
        ? data.frameRecommendedTitle
        : null,
      frameRecommendedLabel: data?.frameRecommendedLabel
        ? data.frameRecommendedLabel
        : null,
    };
  };

  useEffect(() => {
    if (!mainDraw) {
      setMainDrawEnriched(undefined);
      return;
    }

    if (isDrawEnriched(mainDraw)) {
      setMainDrawEnriched(mainDraw);
    } else {
      enrichDraw(mainDraw).then(setMainDrawEnriched);
    }
  }, [mainDraw]);

  useEffect(() => {
    if (linkedDraws.every(isDrawEnriched)) {
      setLinkedDrawsEnriched(linkedDraws.filter(isDrawEnriched));
      return;
    }

    const enrichPromises = linkedDraws.map((draw) =>
      isDrawEnriched(draw) ? Promise.resolve(draw) : enrichDraw(draw),
    );

    Promise.all(enrichPromises).then(setLinkedDrawsEnriched);
  }, [linkedDraws]);

  useEffect(() => {
    if (selectedForDrawIdRef.current === mainDraw?.draw.id) return;

    selectedForDrawIdRef.current = mainDraw?.draw.id;
    setSelectedDraws(mainDraw?.draw.id ? [mainDraw.draw.id] : []);
  }, [mainDraw?.draw.id]);

  useEffect(() => {
    setDrawEntryStatus((prevStatus) => {
      const newStatus = { ...prevStatus };
      let changed = false;
      allDraws.forEach((draw) => {
        if (draw?.draw.id) {
          const existing = newStatus[draw.draw.id];
          const newEntered = draw.draw.accountIsInDraw ?? false;
          const newBid = draw.draw.accountAuctionBid
            ? {
                amount: draw.draw.accountAuctionBid.amount,
                currencyCode: draw.draw.accountAuctionBid.currencyCode,
              }
            : undefined;

          if (
            !existing ||
            existing.entered !== newEntered ||
            existing.accountAuctionBid?.amount !== newBid?.amount ||
            existing.accountAuctionBid?.currencyCode !== newBid?.currencyCode
          ) {
            newStatus[draw.draw.id] = {
              entered: newEntered,
              accountAuctionBid: newBid,
            };
            changed = true;
          }
        }
      });
      return changed ? newStatus : prevStatus;
    });
  }, [allDraws]);

  const updateDrawEntryStatus = useCallback(
    (drawId?: string) => {
      let draws = allDraws;
      if (drawId) {
        draws = draws.filter((draw) => draw?.draw.id === drawId);
      }

      const productIds = draws
        .filter((draw): draw is NonNullable<typeof draw> =>
          Boolean(draw?.product?.shopifyId),
        )
        .map((draw) => draw.product.shopifyId);

      if (productIds.length === 0) return;

      drawGateway.getDrawStatusesByProductIds(productIds).then((entries) => {
        if (!entries) return;

        setDrawEntryStatus((prevStatus) => {
          let hasChanged = false;
          const nextStatus = { ...prevStatus };

          entries.forEach((draw) => {
            if (!draw) return;

            const existing = prevStatus[draw.id];
            const newEntered = draw.accountIsInDraw;
            const newAmount = draw.accountAuctionBid?.amount;
            const newCurrency = draw.accountAuctionBid?.currencyCode;

            if (
              !existing ||
              existing.entered !== newEntered ||
              existing.accountAuctionBid?.amount !== newAmount ||
              existing.accountAuctionBid?.currencyCode !== newCurrency
            ) {
              hasChanged = true;
              nextStatus[draw.id] = {
                entered: newEntered,
                accountAuctionBid: draw.accountAuctionBid
                  ? {
                      amount: draw.accountAuctionBid.amount,
                      currencyCode: draw.accountAuctionBid.currencyCode,
                    }
                  : undefined,
              };
            }
          });

          return hasChanged ? nextStatus : prevStatus;
        });

        setCurrentWinningBidByDrawId((prevStatus) => {
          let hasChanged = false;
          const nextStatus = { ...prevStatus };

          entries.forEach((draw) => {
            if (!draw) return;

            const existing = prevStatus[draw.id];
            const newFloor = draw.auctionWinningFloorPrice;

            if (
              existing?.amount !== newFloor?.amount ||
              existing?.currencyCode !== newFloor?.currencyCode
            ) {
              hasChanged = true;
              nextStatus[draw.id] = newFloor
                ? {
                    amount: newFloor.amount,
                    currencyCode: newFloor.currencyCode,
                  }
                : undefined;
            }
          });

          return hasChanged ? nextStatus : prevStatus;
        });
      });
    },
    [allDraws, drawGateway],
  );

  const updateCurrentWinningBidByDrawId = useCallback(
    (
      drawId: string,
      winningFloorPrice: { amount: string; currencyCode: string } | undefined,
    ) => {
      setCurrentWinningBidByDrawId((prevStatus) => {
        const existing = prevStatus[drawId];
        if (
          existing?.amount === winningFloorPrice?.amount &&
          existing?.currencyCode === winningFloorPrice?.currencyCode
        ) {
          return prevStatus;
        }
        return {
          ...prevStatus,
          [drawId]: winningFloorPrice,
        };
      });
    },
    [setCurrentWinningBidByDrawId],
  );

  useEffect(() => {
    if (user) {
      updateDrawEntryStatus();
    }
  }, [user, updateDrawEntryStatus]);

  const isAccountInDraw = useCallback(
    (drawId: string) =>
      (drawEntryStatus[drawId]?.entered ?? false) ||
      Boolean(drawEntries[drawId]?.entered),
    [drawEntryStatus, drawEntries],
  );

  const accountAuctionBid = useCallback(
    (drawId: string) =>
      drawEntries[drawId]?.bid ?? drawEntryStatus[drawId]?.accountAuctionBid,
    [drawEntryStatus, drawEntries],
  );

  const drawIdsEntered = useMemo(() => {
    return [
      ...(mainDrawEnriched?.draw.id ? [mainDrawEnriched.draw.id] : []),
      ...linkedDrawsEnriched.map((draw) => draw.draw.id),
    ].filter((drawId) => isAccountInDraw(drawId));
  }, [mainDrawEnriched, linkedDrawsEnriched, isAccountInDraw]);

  const context = useMemo(
    () => ({
      mainDraw: mainDrawEnriched,
      linkedDraws: linkedDrawsEnriched,
      isAccountInDraw,
      selectedDraws,
      setSelectedDraws,
      selectDraw: (drawId: string) => {
        setSelectedDraws((prev) => {
          if (prev.includes(drawId)) {
            return prev.filter((id) => id !== drawId);
          }
          return [...prev, drawId];
        });
      },
      drawIdsEntered,
      winningDrawsCount,
      setWinningDrawsCount,
      accountAuctionBid,
      currentWinningBidByDrawId,
      updateCurrentWinningBidByDrawId,
      updateDrawEntryStatus,
      drawEntriesInSession,
      addDrawEntryToSession: (
        drawEntry: PreAuthDrawController['entryIntentResponse'],
      ) => {
        setDrawEntriesInSession((prev) => [...prev, drawEntry]);
      },
    }),
    [
      mainDrawEnriched,
      linkedDrawsEnriched,
      isAccountInDraw,
      selectedDraws,
      drawIdsEntered,
      winningDrawsCount,
      accountAuctionBid,
      currentWinningBidByDrawId,
      updateCurrentWinningBidByDrawId,
      updateDrawEntryStatus,
      drawEntriesInSession,
      setDrawEntriesInSession,
    ],
  );

  return (
    <DrawContext.Provider value={context}>{children}</DrawContext.Provider>
  );
};

export const useDraws = () => useContext(DrawContext);
